import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { z } from 'zod'
import { SummaryDaySchema, summaryLocalDay, type SummarizerCardSchema } from '../schemas/sessionSummarizers'
import { useGetSessionSummarizersQuery, useGetSessionSummaryQuery } from '../services/sessionSummarizersApi'
import SessionSummaryResult from './SessionSummaryResult'
import './SessionSummarizers.css'

type Card = z.infer<typeof SummarizerCardSchema>

function adjacentDay(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86400000).toISOString().slice(0, 10)
}

function summaryDayParam(id: string) { return `summaryDay-${id}` }

function SummaryCard({ projectId, card, onDefaultDay }: {
  projectId: string; card: Card
  onDefaultDay: (id: string, day: string, search: string) => void
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const parsedDay = SummaryDaySchema.safeParse(searchParams.get(summaryDayParam(card.id)))
  const requestedDay = parsedDay.success ? parsedDay.data : undefined
  const setDay = (selectedDay: string | undefined) => {
    if (!selectedDay) return
    const next = new URLSearchParams(searchParams)
    next.set(summaryDayParam(card.id), selectedDay)
    setSearchParams(next)
  }
  const today = summaryLocalDay(card.timezone)
  const yesterday = adjacentDay(today, -1)
  const query = useGetSessionSummaryQuery({ projectId, id: card.id, day: requestedDay }, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const history = query.currentData
  const run = history?.run
  const day = requestedDay ?? run?.day ?? yesterday
  const search = searchParams.toString()
  useEffect(() => {
    if (!requestedDay && history) onDefaultDay(card.id, day, search)
  }, [card.id, requestedDay, history, day, search, onDefaultDay])
  function selectDay(value: string) {
    const parsed = SummaryDaySchema.safeParse(value)
    if (!parsed.success || parsed.data < '0001-01-01' || parsed.data > yesterday) return
    setDay(parsed.data)
  }
  return <article className="session-summarizers__card">
    <header className="session-summarizers__card-header">
      <div>
        <h2 className="session-summarizers__card-title">{card.name}</h2>
        <p className="session-summarizers__card-meta">{card.enabled ? `Automatic \u00b7 ${String(card.hour).padStart(2, '0')}:00 ${card.timezone}` : `Manual \u00b7 ${card.timezone}`}</p>
      </div>
      <div className="session-summarizers__navigation" aria-label={`Summary date for ${card.name}`}>
        <button type="button" aria-label={`Previous day for ${card.name}`} disabled={day <= '0001-01-01'} onClick={() => selectDay(adjacentDay(day, -1))}><ChevronLeft size={18} /></button>
        <label>Summary date<input type="date" required min="0001-01-01" max={yesterday} value={day} onChange={e => selectDay(e.target.value)} /></label>
        <button type="button" aria-label={`Next day for ${card.name}`} disabled={day >= yesterday} onClick={() => selectDay(adjacentDay(day, 1))}><ChevronRight size={18} /></button>
        <button type="button" disabled={day === yesterday} onClick={() => selectDay(yesterday)}>Yesterday</button>
      </div>
    </header>
    {query.isFetching && !history && <p role="status">Loading summary for {day}\u2026</p>}
    {query.isError && <p role="alert">Could not load the summary for {day}. <button onClick={() => query.refetch()}>Retry</button></p>}
    {history && !run && !query.isError && <p className="session-summarizers__empty">No summary for {day} yet.</p>}
    {run && <>
      {!run.output && <p role="status">Summary status: {run.status}{run.status === 'failed' && ' \u2014 run this day again to start a fresh summary.'}</p>}
      {run.cutoff && <p className="session-summarizers__muted">{run.reviewed_sessions} of {run.sessions} sessions reviewed{run.completed_at && <> \u00b7 <time dateTime={new Date(run.completed_at).toISOString()}>{new Date(run.completed_at).toLocaleString()}</time></>}</p>}
      {run.output && <SessionSummaryResult run={run} projectId={projectId} />}
    </>}
  </article>
}

export default function SessionSummarizers({ projectId }: { projectId: string }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.toString()
  const [defaultDays, setDefaultDays] = useState<Record<string, { day: string; search: string; projectId: string }>>({})
  const onDefaultDay = useCallback((id: string, day: string, resolvedSearch: string) => {
    setDefaultDays(current => {
      const previous = current[id]
      if (previous?.day === day && previous.search === resolvedSearch && previous.projectId === projectId) return current
      return { ...current, [id]: { day, search: resolvedSearch, projectId } }
    })
  }, [projectId])
  useEffect(() => {
    const next = new URLSearchParams(search)
    for (const [id, resolved] of Object.entries(defaultDays)) {
      if (resolved.projectId === projectId && resolved.search === search) {
        next.set(summaryDayParam(id), resolved.day)
      }
    }
    if (next.toString() !== search) setSearchParams(next, { replace: true })
  }, [defaultDays, projectId, search, setSearchParams])
  const query = useGetSessionSummarizersQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  return <section className="session-summarizers" aria-label="Daily summary">
    {query.isLoading && <p>Loading summary\u2026</p>}
    {query.isError && <p role="alert">Could not load summary. <button onClick={() => query.refetch()}>Retry</button></p>}
    {query.currentData?.summarizers.length === 0 && <p className="session-summarizers__empty">No daily summary configured yet.</p>}
    {query.currentData?.summarizers.map(card => <SummaryCard key={`${projectId}:${card.id}`} onDefaultDay={onDefaultDay} projectId={projectId} card={card} />)}
  </section>
}
